import React, { useState } from "react";

interface EolRecord {
  id: string;
  residentName: string;
  onRegister: boolean;
  status: string;
  preferredPlace: string;
  dnacpr: string;
  dnacprDate: string;
  spiritual: string;
  emotional: string;
  familyWishes: string;
  lpaHealth: string;
  lpaProperty: string;
  anticipatoryMeds: string;
  comfortPain: string;
  comfortNutrition: string;
  comfortSkin: string;
  comfortOral: string;
  reviewDate: string;
  notes: string;
  updatedAt: string;
}

const KEY = "eol_records";

function load(): EolRecord[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function loadResidents(): any[] {
  try {
    return JSON.parse(localStorage.getItem("residents_records") || "[]");
  } catch {
    return [];
  }
}

const empty = (name: string): EolRecord => ({
  id: `eol-${Date.now()}`,
  residentName: name,
  onRegister: true,
  status: "On register",
  preferredPlace: "Own room in the home",
  dnacpr: "Discussed",
  dnacprDate: "",
  spiritual: "",
  emotional: "",
  familyWishes: "",
  lpaHealth: "",
  lpaProperty: "",
  anticipatoryMeds: "Not yet in place",
  comfortPain: "",
  comfortNutrition: "",
  comfortSkin: "",
  comfortOral: "",
  reviewDate: "",
  notes: "",
  updatedAt: new Date().toISOString(),
});

export default function EndOfLife({ user }: { user?: any }) {
  const [records, setRecords] = useState<EolRecord[]>(load);
  const residents = loadResidents();
  const [form, setForm] = useState<EolRecord | null>(null);
  const [message, setMessage] = useState("");

  const save = (next: EolRecord[]) => {
    setRecords(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const startNew = (name: string) => setForm(empty(name));

  const saveForm = () => {
    if (!form || !form.residentName) {
      setMessage("Choose a resident.");
      return;
    }
    const record = { ...form, updatedAt: new Date().toISOString() };
    const exists = records.some(r => r.id === record.id);
    save(exists ? records.map(r => (r.id === record.id ? record : r)) : [record, ...records]);
    setForm(null);
    setMessage(`Anticipatory care plan saved for ${record.residentName}.`);
  };

  const remove = (id: string) => save(records.filter(r => r.id !== id));

  const daysTo = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

  const printPlan = (r: EolRecord) => {
    const rows = [
      ["Resident", r.residentName],
      ["Status", r.status],
      ["Preferred place of care", r.preferredPlace],
      ["DNACPR", `${r.dnacpr}${r.dnacprDate ? ` (${r.dnacprDate})` : ""}`],
      ["Spiritual / religious needs", r.spiritual || "-"],
      ["Emotional support", r.emotional || "-"],
      ["Family / key person wishes", r.familyWishes || "-"],
      ["LPA Health & Welfare", r.lpaHealth || "-"],
      ["LPA Property & Affairs", r.lpaProperty || "-"],
      ["Anticipatory (just-in-case) meds", r.anticipatoryMeds],
      ["Comfort - pain", r.comfortPain || "-"],
      ["Comfort - nutrition & hydration", r.comfortNutrition || "-"],
      ["Comfort - skin", r.comfortSkin || "-"],
      ["Comfort - oral care", r.comfortOral || "-"],
      ["Review date", r.reviewDate || "-"],
    ].map(row => `<tr><td style="padding:6px;border-bottom:1px solid #eee;font-weight:bold;width:40%">${row[0]}</td><td style="padding:6px;border-bottom:1px solid #eee">${row[1]}</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Anticipatory Care Plan</title><style>body{font-family:Arial;margin:40px;color:#1f2937}h2{color:#123B36}table{width:100%;border-collapse:collapse;font-size:14px}</style></head><body><h2>Anticipatory & End-of-Life Care Plan</h2><p>Roop Cottage Residential Care Home - ${r.residentName} - updated ${new Date(r.updatedAt).toLocaleDateString()}</p><table>${rows}</table><p style="margin-top:16px;font-size:12px">Person-centred, reviewed regularly, and shared with family and professionals with consent.</p></body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  const set = (key: keyof EolRecord, value: any) => setForm(f => (f ? { ...f, [key]: value } : f));

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">End-of-Life & Anticipatory Care</h2>
        <p className="text-sm text-slate-600">Dignity, choice and personalised comfort care at the end of life - a recognised Outstanding differentiator.</p>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {form ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-slate-900">Anticipatory care plan - {form.residentName}</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                <option>Not on register</option><option>On register</option><option>Last days of life</option><option>Bereavement</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Preferred place of care</label>
              <select value={form.preferredPlace} onChange={e => set("preferredPlace", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                <option>Own room in the home</option><option>Elsewhere in the home</option><option>Hospice</option><option>Hospital</option><option>Family home</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">DNACPR</label>
              <select value={form.dnacpr} onChange={e => set("dnacpr", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                <option>Not discussed</option><option>Discussed</option><option>In place - for resuscitation</option><option>In place - not for resuscitation</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">DNACPR date</label>
              <input type="date" value={form.dnacprDate} onChange={e => set("dnacprDate", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">LPA Health & Welfare</label>
              <input value={form.lpaHealth} onChange={e => set("lpaHealth", e.target.value)} placeholder="Name / status" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">LPA Property & Affairs</label>
              <input value={form.lpaProperty} onChange={e => set("lpaProperty", e.target.value)} placeholder="Name / status" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Anticipatory (just-in-case) meds</label>
              <select value={form.anticipatoryMeds} onChange={e => set("anticipatoryMeds", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                <option>Not yet in place</option><option>In place</option><option>Not indicated</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Review date</label>
              <input type="date" value={form.reviewDate} onChange={e => set("reviewDate", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <div className="md:col-span-2"><label className="mb-1 block text-xs font-medium text-slate-600">Spiritual / religious needs</label><textarea value={form.spiritual} onChange={e => set("spiritual", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" /></div>
            <div className="md:col-span-2"><label className="mb-1 block text-xs font-medium text-slate-600">Emotional & psychological support</label><textarea value={form.emotional} onChange={e => set("emotional", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" /></div>
            <div className="md:col-span-2"><label className="mb-1 block text-xs font-medium text-slate-600">Family / key person wishes</label><textarea value={form.familyWishes} onChange={e => set("familyWishes", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Comfort - pain</label><textarea value={form.comfortPain} onChange={e => set("comfortPain", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Comfort - nutrition & hydration</label><textarea value={form.comfortNutrition} onChange={e => set("comfortNutrition", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Comfort - skin</label><textarea value={form.comfortSkin} onChange={e => set("comfortSkin", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Comfort - oral care</label><textarea value={form.comfortOral} onChange={e => set("comfortOral", e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" /></div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={saveForm} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save care plan</button>
            <button onClick={() => setForm(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-slate-900">Start a care plan</h3>
          <select defaultValue="" onChange={e => { if (e.target.value) startNew(e.target.value); }} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
            <option value="">Choose resident</option>
            {residents.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>
        </div>
      )}

      <div className="space-y-3">
        {records.map(r => {
          const due = r.reviewDate ? daysTo(r.reviewDate) : null;
          return (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{r.residentName} <span className="text-xs text-slate-500">{r.status} | {r.preferredPlace}</span></div>
                <div className="flex items-center gap-2">
                  {due !== null ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${due < 0 ? "bg-red-100 text-red-700" : due <= 30 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{due < 0 ? `Review overdue ${Math.abs(due)}d` : `Review in ${due}d`}</span> : null}
                  <button onClick={() => setForm(r)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Edit</button>
                  <button onClick={() => printPlan(r)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Print</button>
                  <button onClick={() => remove(r.id)} className="rounded-lg border border-red-300 px-3 py-1 text-sm text-red-700">Remove</button>
                </div>
              </div>
              <div className="mt-1 text-xs text-slate-600">DNACPR: {r.dnacpr} | JIC meds: {r.anticipatoryMeds} | LPA: {r.lpaHealth || "none recorded"}</div>
            </div>
          );
        })}
        {records.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No anticipatory care plans yet. Start one above for any resident approaching end of life.</div> : null}
      </div>
    </div>
  );
}